// The API client. One place that knows the prefix, the CSRF header, and what a signed-out
// response means; every component calls through it and never fetches directly.

const PREFIX = '/api/v1';
const CSRF = { 'x-requested-with': 'waag' };

let onUnauthenticated = () => {};

/** The shell registers what to do when the session has gone (see app.js). */
export function setUnauthenticatedHandler(fn) {
  onUnauthenticated = fn;
}

export class ApiError extends Error {
  constructor(message, { status, code, requestId, details } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.requestId = requestId;
    this.details = details;
  }
}

function url(path, params) {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value === null || value === undefined || value === '') continue;
    search.set(key, Array.isArray(value) ? value.join(',') : String(value));
  }
  const query = search.toString();
  return `${PREFIX}${path}${query ? `?${query}` : ''}`;
}

async function request(method, path, { params, body } = {}) {
  const headers = { accept: 'application/json' };
  if (method !== 'GET') {
    Object.assign(headers, CSRF);
    if (body !== undefined) headers['content-type'] = 'application/json';
  }

  const res = await fetch(url(path, params), {
    method,
    headers,
    credentials: 'same-origin',
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (res.status === 401) {
    onUnauthenticated();
    throw new ApiError('Not signed in', { status: 401, code: 'UNAUTHENTICATED' });
  }

  const text = res.status === 204 ? '' : await res.text();
  const payload = text ? JSON.parse(text) : null;
  if (!res.ok) {
    throw new ApiError(payload?.message ?? res.statusText, {
      status: res.status,
      code: payload?.code,
      requestId: payload?.request_id,
      details: payload?.details,
    });
  }
  return payload;
}

export const api = {
  get: (path, params) => request('GET', path, { params }),
  post: (path, body) => request('POST', path, { body }),
  put: (path, body) => request('PUT', path, { body }),
  patch: (path, body) => request('PATCH', path, { body }),
  delete: (path) => request('DELETE', path),
};

/** Where to send a visitor who needs to sign in, coming back to where they were. */
export function signInUrl(next = window.location.pathname + window.location.search) {
  return `${PREFIX}/auth/google/start?next=${encodeURIComponent(next)}`;
}
